import { getIframeSessionToken, getSessionToken } from './account'
import { store } from '../store'
import { bindActionToPromise } from '../actions/utils'
import authActions from '../actions/auth'
import appErrorActions from '../actions/appError'
import EnvHandler from '../utils/env-handler'
import { parseResponseToError } from '../ts-utils/error-parsers'
import { isCanlan } from '../utils/customer-name'
import {
  isValidationError,
  normalizeApiError,
} from '../utils/normalizeApiError'

export const getHeaders = async (
  requiresAuth,
  headers = {},
  skipAutoContentType = false,
  useIframeToken = false
) => {
  const config = { Accept: 'application/json', ...headers }

  // The browser takes care of setting this header in some cases automatically,
  // like when submitting a form
  if (!skipAutoContentType) {
    config['Content-Type'] = 'application/json'
  }

  if (requiresAuth) {
    config.Authorization = `Bearer ${
      useIframeToken ? await getIframeSessionToken() : await getSessionToken()
    }`
  }

  for (const header in headers) {
    if (
      Object.prototype.hasOwnProperty.call(config, header) &&
      config[header] === false
    ) {
      // Remove header values that have been set explicitly to false
      delete config[header]
    }
  }

  return new Headers(config)
}

const buildQueryString = (params = {}) => {
  const keyNames = params ? Object.keys(params) : []
  if (keyNames.length === 0) {
    return ''
  }

  const keys = []
  for (let i = 0; i < keyNames.length; i++) {
    const keyName = keyNames[i]
    const value = params[keyName]
    if (typeof value === 'undefined') {
      continue
    }
    keys.push(`${encodeURIComponent(keyName)}=${encodeURIComponent(value)}`)
  }

  if (keys.length === 0) {
    return ''
  }

  return `?${keys.join('&')}`
}

let cachedInFrame = false
let cachedEnv = null

/**
 * `settings` is the fetch options bag (already stripped of the req-only keys).
 * `retry` is the TOTAL number of transport attempts, not extra ones.
 *
 * When `reportGlobalError` is false, transport failures are thrown to the
 * caller WITHOUT also being dispatched to the global app-error banner. A bulk
 * operation that renders its own per-row failure panel must not also blast a
 * modal per failed row.
 *
 * Deliberately NOT annotated with `@param` tags: `settings` is an open bag and
 * a JSDoc type here would close it, breaking every caller that passes `query`,
 * `method`, `body` or `timeout`.
 */
const request = async (
  url,
  settings = {},
  retry = 3,
  reportGlobalError = true
) => {
  let env

  if (cachedEnv) {
    env = cachedEnv
  } else {
    const environment = await EnvHandler.get()
    env = environment
    cachedEnv = env
  }

  const {
    requiresAuth = true,
    method = 'GET',
    body = false,
    headers,
    query,
    skipAutoContentType,
    useIframeToken,
    timeout,
    ...rest
  } = settings

  if (typeof useIframeToken === 'boolean') {
    cachedInFrame = useIframeToken
  }

  let abortController, signal
  if (timeout) {
    abortController = new AbortController()
    signal = abortController.signal
  }

  const options = {
    headers: await getHeaders(
      requiresAuth,
      headers,
      skipAutoContentType,
      cachedInFrame
    ),
    method,
    signal,
    ...rest,
  }

  if (body) {
    options.body = body
  }

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  for (let attempt = 0; attempt < retry; attempt++) {
    try {
      const queryString = buildQueryString(query)
      let to
      if (timeout) {
        to = setTimeout(() => abortController.abort(), timeout)
      }
      const response = await fetch(env + url + queryString, options)
      if (to) {
        clearTimeout(to)
      }
      if (response?.status === 504) {
        let message = `Gateway Timeout Error (504) URL:${env + url} (${method})`
        const error = new Error(message)
        error.name = 'GatewayTimeoutError'
        error.status = 504
        error.url = env + url
        error.method = method
        if (reportGlobalError) {
          bindActionToPromise(
            store.dispatch,
            appErrorActions.setError.request
          )(error)
        }
        throw error
      }
      return response
    } catch (e) {
      //  An abort is a deliberate cancellation, never a transient failure.
      //  `abortController` lives OUTSIDE this loop, so once it has fired every
      //  remaining attempt fetches an already-aborted signal and rejects
      //  instantly — the retries buy nothing and only add a second of sleep
      //  each before surfacing the error. Externally-supplied signals (React
      //  Query unmounting a query, a superseded reconciliation poll) must not
      //  be retried at all: nobody is waiting on the result.
      if (options.signal?.aborted || e?.name === 'AbortError') {
        throw e
      }

      if (attempt === retry - 1) {
        let message = `Error: ${e?.message} URL:${env + url} (${method})`
        if (e?.message?.includes('Failed to fetch')) {
          e.name = 'FailedToFetchError'
          e.originalErrorMessage = e?.message || 'Unknown error'
          e.url = env + url
          e.method = method
          e.title = `Unable to connect to ${
            isCanlan ? 'Canlan Stats' : 'SportNinja'
          }.`
          e.description =
            'This is typically caused by firewall, DNS, or network settings on your device or network. Click here for troubleshooting steps or contact your IT administrator for assistance.'
          e.supportUrl = isCanlan
            ? 'https://faq.canlansports.com/faq-category/canlan-stats-app'
            : 'https://support.sportninja.com/'
          message =
            'Unable to connect to SportNinja. View our support page or contact your IT administrator for assistance.'
          if (reportGlobalError) {
            bindActionToPromise(
              store.dispatch,
              appErrorActions.setError.request
            )(e)
          }
        }
        e.message = message
        throw e
      }
      await sleep(1000) // Wait for 1 second before retrying
    }
  }
}

const attemptRefresh = async (response, ...requestProps) => {
  if (!store || !store.dispatch) {
    return
  }

  // Attempt to refresh the session, but only if this isn't already an
  // attempt to refresh the session :P
  // In that case, just log the user out, there's nothing more we can do here
  if (/\/auth\/refresh$/i.test(response.url)) {
    store.dispatch(authActions.logout.request())
    return
  }

  // Attempt to refresh the token; if it fails for whatever reason (i.e. there is
  // no token) then just log the user out
  try {
    await bindActionToPromise(store.dispatch, authActions.refresh.request)()
  } catch (e) {
    store.dispatch(authActions.logout.request())
    return
  }

  return await request(...requestProps)
}

const throwOnHTTPError = async (origResponse, ...requestProps) => {
  const status = origResponse.status
  if (status < 400) {
    return origResponse
  }

  //  requestProps mirrors `request`'s argument list so attemptRefresh can
  //  replay the call verbatim; the 4th is the caller's global-error preference.
  const reportGlobalError = requestProps[3] !== false

  // Any error code greater than 500
  if (status > 500) {
    try {
      const networkError = await parseResponseToError(origResponse)
      if (reportGlobalError) {
        bindActionToPromise(
          store.dispatch,
          appErrorActions.setError.request
        )(networkError)
      }
      throw networkError
    } catch (e) {
      console.error('throwOnHTTPError: Error parsing response', e)
      if (reportGlobalError) {
        bindActionToPromise(store.dispatch, appErrorActions.setError.request)(e)
      }
      throw e
    }
  }

  let error = new Error()
  try {
    error = await origResponse.json()
    if (typeof error === 'string') {
      error = new Error(error)
    }
    error.statusCode = status

    // Normalize validation errors from new format (422/errors) to old format (invalid_fields)
    if (isValidationError(status)) {
      error = normalizeApiError(error)
    }
  } catch (e) {
    if (typeof e === 'string') {
      error = new Error(e)
    }
    // If for some reason the API returns malformed JSON, show that I guess?
    error.message = `There was a problem: ${e?.message || e} ${
      origResponse?.url || ''
    } (${origResponse?.status || ''})`
  }

  if (origResponse && origResponse.url && origResponse.status) {
    error.url = origResponse.url
    error.status = origResponse.status
  }
  error.isApiError = true

  // If error status is 401, we must add a custom message
  if (origResponse?.status === 401) {
    error.message = 'Authentication required. Please log in to continue.'
    // log the user out
    // store.dispatch(authActions.logout.request())
  }

  throw error
}

/**
 * `settings` carries fetch options plus these req-only keys:
 *
 * - `parseJSON` (default true)
 * - `noThrow` (default false)
 * - `maxAttempts` / `transportRetries` (default 3, aliases) — TOTAL transport
 *   attempts. Pass 1 for anything the caller already retries or reconciles
 *   itself, otherwise a three-poll reconciliation quietly becomes nine
 *   transport attempts.
 * - `reportGlobalError` (default true) — pass false when the caller renders its
 *   own failure surface.
 *
 * All of these are stripped here, so they never reach the fetch options.
 *
 * Deliberately NOT annotated with `@param` tags — see `request` above.
 */
export const req = async (url, settings = {}) => {
  const {
    parseJSON = true,
    noThrow = false,
    // Total transport attempts (default 3). `transportRetries` is an older
    // alias for the same thing; both mean TOTAL attempts, not extra ones.
    // Non-idempotent calls (the balance slot POSTs, the game mutations) pass 1
    // so a committed-but-dropped response is never silently re-sent — a retried
    // insert would duplicate server state.
    maxAttempts,
    transportRetries,
    reportGlobalError = true,
    ...rest
  } = settings

  //  Math.max(1, NaN) is NaN, which would make the retry loop never run.
  const parsedAttempts = Number(maxAttempts ?? transportRetries)
  const attempts =
    Number.isFinite(parsedAttempts) && parsedAttempts >= 1
      ? Math.floor(parsedAttempts)
      : 3

  const orig = await request(url, rest, attempts, reportGlobalError)

  // HTTP 204 - No content: just return early
  if (orig.status === 204) {
    return {}
  }

  // HTTP Error codes
  if (noThrow) {
    if (parseJSON) {
      return await orig.json()
    } else {
      return orig
    }
  }

  const response = await throwOnHTTPError(
    orig,
    url,
    rest,
    attempts,
    reportGlobalError
  )

  if (parseJSON) {
    return await response.json()
  } else {
    return response
  }
}

export default req
